import * as utils from '../data/utils.js';
import * as http from '../utils/http.js';

function defaultStatusValidator(status) {
    return (status >= 200 && status < 300) || status === 302;
}

function isEnvelope(payload) {
    return !!(
        payload &&
        typeof payload === 'object' &&
        typeof payload.type === 'string' &&
        typeof payload.status === 'number'
    );
}

function defaultResponseNormalizer(response) {
    const body = response.payload;
    response.body = body;

    if (!isEnvelope(body)) {
        response.type = 'raw';
        return response;
    }

    response.envelope = body;
    response.type = body.type;
    response.envelopeStatus = body.status;
    response.payload = Object.hasOwn(body, 'payload') ? body.payload : null;

    if (body.status !== response.status) {
        response.ok = false;
        response.contractError = {
            code: 904000001,
            message: 'Server response status does not match HTTP status.',
        };
    }

    if (body.type === 'error') {
        response.ok = false;
    }

    return response;
}

async function authRefreshRetryMiddleware({ request, doFetch, next }) {
    const httpResponse = await next();

    if (
        httpResponse.status !== 401 ||
        request.skipAuthRefresh ||
        !request.auth ||
        (request.auth.type !== 'bearer' && request.auth.type !== 'cookie') ||
        !request.auth.manager ||
        request._retryAfterRefresh
    ) {
        return httpResponse;
    }

    const manager = request.auth.manager;
    await manager.refresh();

    if (request.auth.type === 'bearer') {
        const newToken = manager.getAccessToken?.();
        if (!newToken) {
            return httpResponse;
        }
        request.init.headers.Authorization = `Bearer ${newToken}`;
    }

    request._retryAfterRefresh = true;
    return doFetch();
}

export class RemoteService {

    constructor(config = {}) {
        const {
            baseUrl = '',
            defaultHeaders = {},
            defaultContentType = 'auto',
            defaultResponseType = 'auto',
            defaultResponseTypeMode = 'lenient',
            defaultCorsPolicy = 'same-origin',
            defaultResponseValidator = defaultStatusValidator,
            defaultAuth,
            timeout = 15000,
            authManager = null,
            middlewares = [authRefreshRetryMiddleware],
            responseNormalizer = defaultResponseNormalizer,
        } = config;

        this.baseUrl = baseUrl.replace(/\/+$/, '');
        this.defaultHeaders = { ...defaultHeaders };
        this.defaultContentType = defaultContentType;
        this.defaultResponseType = defaultResponseType;
        this.defaultResponseTypeMode = defaultResponseTypeMode;
        this.defaultCorsPolicy = defaultCorsPolicy;
        this.defaultResponseValidator = defaultResponseValidator ?? defaultStatusValidator;
        this.defaultAuth = defaultAuth;
        this.timeout = timeout;
        this.authManager = authManager;
        this.middlewares = Array.isArray(middlewares) ? [...middlewares] : [];
        this.responseNormalizer = typeof responseNormalizer === 'function' ? responseNormalizer : defaultResponseNormalizer;
    }

    setDefaultHeader(name, value) {
        if (value == null) delete this.defaultHeaders[name];
        else this.defaultHeaders[name] = value;
    }

    useMiddleware(middleware) {
        if (typeof middleware === 'function') {
            this.middlewares.push(middleware);
        }
    }

    async call(method, url, data = null, options = {}) {
        const request = this._prepareRequest(method, url, data, options);
        const httpResponse = await this._executeCall(request);
        let response = await this._parseResponse(request, httpResponse);
        response = this.responseNormalizer(response, request) ?? response;

        if (!response.ok) throw response;
        return response;
    }

    _prepareRequest(method, url, data = null, options = {}) {
        let {
            headers,
            contentType,
            responseType,
            responseTypeMode,
            responseValidator,
            corsPolicy,
            timeout,
            auth,
            skipAuthRefresh = false,
        } = options;

        contentType ??= this.defaultContentType;
        responseType ??= this.defaultResponseType;
        responseTypeMode ??= this.defaultResponseTypeMode;
        responseValidator = (responseValidator && typeof responseValidator === 'function')
            ? responseValidator
            : this.defaultResponseValidator;
        timeout ??= this.timeout;
        corsPolicy ??= this.defaultCorsPolicy;

        let effectiveAuth = auth || null;
        if (!effectiveAuth) {
            if (this.authManager) {
                effectiveAuth = this.authManager.isCookieMode()
                    ? { type: 'cookie', manager: this.authManager }
                    : { type: 'bearer', manager: this.authManager };
            } else if (this.defaultAuth) {
                effectiveAuth = this.defaultAuth;
            }
        }

        let effectiveUrl = url;
        if (this.baseUrl && !/^https?:\/\//i.test(url)) {
            if (url !== '' && !url.startsWith('/')) url = `/${url}`;
            effectiveUrl = this.baseUrl + url;
        }

        const request = {
            url: effectiveUrl,
            init: {
                method: method?.toUpperCase() ?? 'GET',
                headers: {
                    ...this.defaultHeaders,
                    ...(headers || {}),
                },
                mode: corsPolicy,
            },
            timeout,
            responseType,
            responseTypeMode,
            responseValidator,
            auth: effectiveAuth,
            skipAuthRefresh,
            _retryAfterRefresh: false,
        };

        switch (contentType) {
            case 'json':
                request.init.headers['Content-Type'] = 'application/json; charset=utf-8';
                break;
            case 'text':
                request.init.headers['Content-Type'] = 'text/*';
                break;
            default:
                break;
        }

        switch (responseType) {
            case 'json':
                request.init.headers.Accept = 'application/json';
                break;
            case 'text':
                request.init.headers.Accept = 'text/*';
                break;
            default:
                break;
        }

        if (effectiveAuth) {
            switch (effectiveAuth.type) {
                case 'bearer': {
                    const tokenFromOptions = effectiveAuth.token;
                    const tokenFromManager = effectiveAuth.manager?.getAccessToken?.();
                    const bearerToken = tokenFromOptions ?? tokenFromManager ?? '';
                    if (bearerToken) {
                        request.init.headers.Authorization = `Bearer ${bearerToken}`;
                        request.init.credentials = 'same-origin';
                    }
                    break;
                }
                case 'cookie':
                    request.init.credentials = 'include';
                    break;
                case 'basic': {
                    const username = effectiveAuth.username || '';
                    const password = effectiveAuth.password || '';
                    request.init.headers.Authorization = `Basic ${btoa(`${username}:${password}`)}`;
                    break;
                }
                default:
                    break;
            }
        }

        if (data != null) {
            if (request.init.method === 'GET' || request.init.method === 'HEAD') {
                let query = null;
                if (utils.isObject(data)) query = http.encodeQueryString(data);
                else if (utils.isString(data)) query = data;

                if (query) {
                    const separator = effectiveUrl.includes('?') ? '&' : '?';
                    request.url = `${effectiveUrl}${separator}${query}`;
                }
            } else if (contentType === 'json') {
                request.init.body = JSON.stringify(data);
            } else if (data !== null && data !== undefined) {
                request.init.body = data;
            }
        }

        return request;
    }

    async _executeCall(request) {
        const controller = new AbortController();
        request.init.signal = controller.signal;

        const timeoutId = setTimeout(() => controller.abort(), request.timeout);
        const doFetch = async () => fetch(request.url, request.init);

        try {
            const pipeline = this.middlewares.reduceRight(
                (next, middleware) => async () => middleware({ request, doFetch, next }),
                doFetch,
            );
            return await pipeline();
        } finally {
            clearTimeout(timeoutId);
        }
    }

    async _parseResponse(request, httpResponse) {
        const response = {
            ok: request.responseValidator(httpResponse.status),
            status: httpResponse.status,
            statusText: httpResponse.statusText,
            httpResponse,
            payload: null,
            payloadType: 'empty',
        };

        if (httpResponse.bodyUsed) return response;

        if (httpResponse.status === 204 || httpResponse.status === 304) {
            return response;
        }

        if (httpResponse.headers.has('Content-Length')) {
            const payloadLength = parseInt(httpResponse.headers.get('Content-Length'), 10);
            if (!Number.isNaN(payloadLength) && payloadLength <= 0) return response;
        }

        const hint = request.responseType || 'auto';
        const mode = request.responseTypeMode || 'lenient';
        const httpContentType = httpResponse.headers.has('Content-Type')
            ? httpResponse.headers.get('Content-Type')
            : 'text/plain';
        const normalizedContentType = httpContentType.toLowerCase();

        let payloadType = hint;
        if (hint === 'auto') {
            if (normalizedContentType.includes('application/json')) payloadType = 'json';
            else if (normalizedContentType.includes('text/xml') || normalizedContentType.includes('application/xml')) payloadType = 'xml';
            else if (normalizedContentType.includes('text')) payloadType = 'text';
            else if (normalizedContentType.includes('image') || normalizedContentType.includes('video') || normalizedContentType.includes('application/octet-stream')) payloadType = 'blob';
            else if (normalizedContentType.includes('multipart/form-data')) payloadType = 'form';
            else payloadType = 'buffer';
        }

        response.payloadType = payloadType;

        switch (payloadType) {
            case 'text':
                response.payload = await httpResponse.text();
                break;
            case 'json': {
                const raw = await httpResponse.text();
                try {
                    response.payload = raw ? JSON.parse(raw) : null;
                } catch (err) {
                    if (mode === 'strict') {
                        err.response = response;
                        err.raw = raw;
                        throw err;
                    }
                    response.parseError = err;
                    response.payloadRaw = raw;
                    response.payloadType = 'text';
                    response.payload = raw;
                }
                break;
            }
            case 'xml': {
                const domtext = await httpResponse.text();
                try {
                    response.payload = new window.DOMParser().parseFromString(domtext, 'application/xml');
                } catch (err) {
                    if (mode === 'strict') {
                        err.response = response;
                        err.raw = domtext;
                        throw err;
                    }
                    response.parseError = err;
                    response.payloadRaw = domtext;
                    response.payloadType = 'text';
                    response.payload = domtext;
                }
                break;
            }
            case 'blob':
                response.payload = await httpResponse.blob();
                break;
            case 'form':
                response.payload = await httpResponse.formData();
                break;
            default:
                response.payload = await httpResponse.arrayBuffer();
                break;
        }

        return response;
    }

    async get(url, data, options) { return this.call('GET', url, data, options); }

    async put(url, data, options) { return this.call('PUT', url, data, options); }

    async post(url, data, options) { return this.call('POST', url, data, options); }

    async delete(url, data, options) { return this.call('DELETE', url, data, options); }
}

export { authRefreshRetryMiddleware, defaultResponseNormalizer, isEnvelope };
